import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientLocationHydrated, ClientLocationWithLevel } from "api-types/locations/locationsResponses";
import type { ClientUserHydrated } from "api-types/users/usersResponses";
import pluralize from "common/utils/pluralize";
import Toggle from "lib/components/actions/Toggle";
import SectionHeader from "lib/components/builders/communities/plans/SectionHeader";
import SearchBar from "lib/components/builders/communities/plans/showroom/SearchBar";
import { getLocationIconForType } from "lib/components/builders/getLocationIcon";
import { getBuilderUsersURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { locationDefaultPath, locationHomePlansPath, locationLotsPath, locationPlanListPath } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import getUserFullName from "lib/utils/userName";
import type { ReactElement, ReactNode } from "react";
import React, { useMemo, useState } from "react";
import Link from "ui/components/Link";
import cx from "ui/cx";
import IconLineArrow from "ui/icons/IconLineArrow";
import Tooltip from "ui/popovers/Tooltip";
import EmptySearchResults from "ui/search/EmptySearchResults";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column, ExpandedState, PaginationState, Row } from "ui/tables/DataTable/types";
import styles from "./CommunitiesTable.module.scss";
import CommunityDropdown, { CommunityDropdownModals, useCommunityDropdownModalState } from "./modals/CommunityDropdown";
import {
  canViewChildLocations,
  filterLocationsBySearch,
  getLocationLotCountWithDescendants,
} from "./utils/locationUtils";

interface CommunityTableProps {
  builder: ClientBuilder;
  locationName?: string;
  locations: ClientLocationWithLevel[];
  pagination?: { state: PaginationState; setPagination: (state: PaginationState) => void; totalCount: number };
  onSuccess: (location: ClientLocationWithLevel) => void;
  emptyCTA?: ReactNode;
  subTable?: (row: Row<ClientLocationWithLevel>) => ReactNode;
  showColumnHeaders?: boolean;
  getRowCanExpand?: (row: Row<ClientLocationWithLevel>) => boolean;
  search?: string;
  setSearch?: (s: string) => void;
  forcedExpanded?: Record<string, boolean>;
  showArchived?: boolean;
  setShowArchived?: (show: boolean) => void;
  homePlanCountsWithDescendants: Record<string, number>;
}

interface ClientLocationWithLevelWithAgent extends ClientLocationWithLevel {
  agent?: ClientUserHydrated;
}

type SubTableWithArchiveProps = (
  row: Row<ClientLocationWithLevel> & {
    showArchived?: boolean;
    setShowArchived?: (b: boolean) => void;
  },
) => React.ReactNode;

export default function CommunitiesTable({
  builder,
  locationName,
  locations,
  pagination,
  onSuccess,
  emptyCTA,
  subTable,
  showColumnHeaders = true,
  getRowCanExpand,
  search: externalSearch,
  setSearch: setExternalSearch,
  forcedExpanded,
  showArchived: externalShowArchived,
  setShowArchived: setExternalShowArchived,
  homePlanCountsWithDescendants: externalHomePlanCounts,
}: CommunityTableProps) {
  const { hasPermission, enabledFeatures } = useUserContext();
  const canViewLocationSummary = enabledFeatures.locationSummary && hasPermission("VIEW_LOCATION_SUMMARY");
  const [selectedCommunity, setSelectedCommunity] = useState<ClientLocationWithLevel | null>(null);
  const [internalSearch, setInternalSearch] = useState("");
  const search = externalSearch ?? internalSearch;
  const setSearch = setExternalSearch ?? setInternalSearch;
  const [internalShowArchived, setInternalShowArchived] = useState(false);
  const showArchived = externalShowArchived ?? internalShowArchived;
  const setShowArchived = setExternalShowArchived ?? setInternalShowArchived;
  const dropdownModalState = useCommunityDropdownModalState();
  const [userExpanded, setUserExpanded] = useState<ExpandedState>({});

  const mergedExpanded = useMemo<ExpandedState>(() => {
    return Object.assign({}, userExpanded, forcedExpanded);
  }, [forcedExpanded, userExpanded]);

  const handleExpandedChange = (updaterOrValue: ExpandedState | ((old: ExpandedState) => ExpandedState)) => {
    const expandedState = typeof updaterOrValue === "function" ? updaterOrValue(userExpanded) : updaterOrValue;

    const userControlledExpansions = Object.fromEntries(
      Object.entries(expandedState).filter(([key]) => !forcedExpanded || !(key in forcedExpanded)),
    );
    setUserExpanded(userControlledExpansions);
  };

  const { data: users } = useFetch<ClientUserHydrated[]>(
    builder ? getBuilderUsersURL({ builderId: builder.id }) : "",
    [builder.id],
    { handleErrorLocally: true },
  );

  const locationsWithAgents = useMemo(() => {
    if (!users) {
      return locations;
    }

    const agentsById = Object.fromEntries(users.map((u) => [u.id, u]));
    return locations.map((l) => ({ ...l, agent: l.salesStaffId ? agentsById[l.salesStaffId] : undefined }));
  }, [locations, users]);

  const locationDescendantsLotsCount = useMemo(() => {
    if (!locations) {
      return;
    }
    return getLocationLotCountWithDescendants(locations);
  }, [locations]);

  const locationDescendantsHomePlanCount = externalHomePlanCounts;

  const columns = useMemo(() => {
    const colDef: Column<ClientLocationWithLevelWithAgent>[] = [
      {
        id: "name",
        field: "name",
        bold: true,
        width: 0.22,
        cell: (ctx) => {
          const icon = getLocationIconForType(ctx.row.original.locationLevel?.type, builder?.locationSettings, {
            size: 16,
          });
          const lotsCount = locationDescendantsLotsCount?.[ctx.row.original.id];
          return (
            <Link
              href={locationDefaultPath(
                ctx.row.original.builderId,
                ctx.row.original.id,
                ctx.row.original.locationLevel?.type,
                ctx.row.original.disableLots,
                !!lotsCount,
                hasPermission("VIEW_LOTS"),
                canViewChildLocations(
                  ctx.row.original.locationLevel?.type,
                  builder?.locationSettings,
                  hasPermission("VIEW_CHILD_LOCATIONS"),
                ),
                enabledFeatures.estimationTasks,
                canViewLocationSummary,
              )}
            >
              {icon} <span>{ctx.row.original.name}</span>
            </Link>
          );
        },
        header: "Name",
      },
      {
        id: "cityState",
        header: "City/State",
        accessor: (c) => [c.city, c.state].filter(Boolean).join(", "),
        width: 0.18,
      },
      {
        id: "lots",
        header: "Lots",
        field: "lotsCount",
        cell: (ctx) => (
          <TableLink
            href={locationLotsPath(ctx.row.original.builderId, ctx.row.original.id)}
            count={ctx.row.original.lotsCount}
            thing="Lot"
            diabledReason={ctx.row.original.disableLots ? "Lots are disabled for this location" : undefined}
          />
        ),
      },
      {
        id: "plans",
        field: "locationPlanCount",
        cell: (ctx) => (
          <TableLink
            href={locationPlanListPath(ctx.row.original.builderId, ctx.row.original.id)}
            count={ctx.row.original.locationPlanCount}
            thing="Plan"
          />
        ),
        header: "Plans",
      },
      {
        id: "homePlans",
        field: "homePlanCount",
        cell: (ctx) => {
          const homePlanCount =
            locationDescendantsHomePlanCount?.[ctx.row.original.id] ?? ctx.row.original.homePlanCount;
          return (
            <TableLink
              href={locationHomePlansPath(ctx.row.original.builderId, ctx.row.original.id)}
              count={homePlanCount}
              thing="Home Plan"
            />
          );
        },
        header: "Home Plans",
      },
      {
        id: "salesStaff",
        header: "Sales Staff",
        accessor: (c) => getUserFullName(c.agent ?? null),
      },
    ];

    if (showArchived) {
      colDef.push({
        id: "archived",
        header: "Archived",
        field: "archivedAt",
        fallback: true,
        type: "DATE",
      });
    }
    return colDef;
  }, [
    showArchived,
    builder?.locationSettings,
    locationDescendantsLotsCount,
    locationDescendantsHomePlanCount,
    enabledFeatures.estimationTasks,
    canViewLocationSummary,
  ]);

  const filteredData: ClientLocationWithLevelWithAgent[] = useMemo(() => {
    // We don't have descendants here, so we pass an empty object
    const emptyDescendantMap: Record<string, ClientLocationHydrated[]> = {};

    return filterLocationsBySearch(locationsWithAgents ?? [], search, emptyDescendantMap, showArchived, forcedExpanded);
  }, [locationsWithAgents, search, showArchived, forcedExpanded]);

  const mapEnabled = builder?.salesConfig?.communityMapConfig?.enabled !== false;
  const salesMapEnabled = builder?.salesConfig?.communityMapConfig?.publicSalesMapEnabled !== false;

  const subTableWithArchiveProps = (
    sub?: SubTableWithArchiveProps,
    showArchived?: boolean,
    setShowArchived?: (b: boolean) => void,
  ) => sub && ((row: Row<ClientLocationWithLevel>) => sub({ ...row, showArchived, setShowArchived }));

  const archiveAwareSubTable = subTableWithArchiveProps(
    subTable,
    showArchived,
    externalShowArchived !== undefined ? setShowArchived : undefined,
  );

  const hideColumnHeadersSubTable =
    archiveAwareSubTable &&
    ((row: Row<ClientLocationWithLevel>) => {
      const el = archiveAwareSubTable(row);

      return React.isValidElement(el)
        ? React.cloneElement(el as ReactElement<{ showColumnHeaders: boolean }>, { showColumnHeaders: false })
        : el;
    });

  const title = locationName ? `Sublocations in ${locationName}` : undefined;

  return (
    <>
      {showColumnHeaders && (
        <div className={styles.tableFilters}>
          <SectionHeader
            title={title}
            headerSearch={<SearchBar id="sublocationsSearch" handleChange={setSearch} marginLeft={title ? 6 : 0} />}
            headerToggle={
              <label className={cx(styles.toggleArchived)}>
                <Toggle isTrue={showArchived} onChange={setShowArchived} variants="small" /> Show Archived
              </label>
            }
            hr
          />
        </div>
      )}
      <ConfigTable
        pagination={pagination?.state}
        setPagination={pagination?.setPagination}
        totalCount={pagination?.totalCount}
        expandedRowState={mergedExpanded}
        onExpandedRowStateChange={handleExpandedChange}
        columns={columns}
        data={filteredData}
        className={styles.configTable}
        getRowHref={(r) => locationHomePlansPath(r.original.builderId, r.original.id)}
        getRowCanExpand={getRowCanExpand}
        rowIdColumn="id"
        subTable={hideColumnHeadersSubTable}
        showColumnHeaders={showColumnHeaders}
        overflowMenu={(row) => (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCommunity(row.original);
            }}
          >
            <CommunityDropdown
              salesMapEnabled={mapEnabled && salesMapEnabled}
              location={row.original}
              modalState={dropdownModalState}
              onSuccess={onSuccess}
              hoverBlue
            />
          </div>
        )}
        emptyCTA={showColumnHeaders ? emptyCTA || <EmptySearchResults /> : null}
      />
      <CommunityDropdownModals location={selectedCommunity} onSuccess={onSuccess} modalState={dropdownModalState} />
    </>
  );
}

function TableLink({
  href,
  count,
  thing,
  diabledReason,
}: {
  href: string;
  count: number;
  thing: string;
  diabledReason?: string;
}) {
  const linkLabel = `${count} ${pluralize(thing, count)}`;

  return (
    <Link href={href} className={styles.metadataLink} disabled={!!diabledReason}>
      {diabledReason ? (
        <Tooltip.Root>
          <Tooltip.Trigger>
            <span>{linkLabel}</span>
          </Tooltip.Trigger>
          <Tooltip.Content>{diabledReason}</Tooltip.Content>
        </Tooltip.Root>
      ) : (
        linkLabel
      )}
      {!diabledReason && <IconLineArrow size={12} />}
    </Link>
  );
}
